'use client'

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useInViewport } from '@/utils/useInViewport'

export type VideoPlayerProps = {
    src: string
    imageSrc?: string
    imageAlt?: string
    playsOnScroll?: boolean
}

/**
 * A video player component. It forwards the `…rest` props to the `<video>` element.
 * You can use `useRef` to have direct access to the `<video>` element.
 */
export const VideoPlayer = forwardRef<
    HTMLVideoElement,
    VideoPlayerProps &
        React.DetailedHTMLProps<React.VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>
>(function VideoPlayer({ src, imageSrc, imageAlt, playsOnScroll, ...rest }, ref) {
    // biome-ignore lint/style/noNonNullAssertion: ref will always exist
    const videoRef = useRef<HTMLVideoElement>(null!)
    const [isPlaying, setPlaying] = useState(false)

    // Because we are using a forwarded ref and local ref we need to resolve that
    // useImperativeHandle handles that situation passing calls to the forwarded
    // ref to the local ref
    // https://reactjs.org/docs/hooks-reference.html#useimperativehandle
    useImperativeHandle(ref, () => videoRef.current)

    // At least 50% of video is visible in viewport
    const inViewport = useInViewport(videoRef, 0.5)

    useEffect(() => {
        if (!videoRef.current) return
        if (!playsOnScroll) return

        if (inViewport) {
            videoRef.current
                .play()
                .then(() => setPlaying(true))
                .catch(() => {
                    // play was rejected
                })
        } else if (isPlaying) {
            videoRef.current.pause()
            setPlaying(false)
        }
    }, [inViewport, playsOnScroll, isPlaying])

    return (
        <video ref={videoRef} {...rest}>
            <source src={src} type="video/mp4" />
            {imageSrc && <img src={imageSrc} alt={imageAlt} />}
        </video>
    )
})
